// ============================================================================
// IssueDetailsModal.tsx
// Popup with description + photos for routine-check NOT_OK items.
// Camera opens live webcam stream (works on laptop AND mobile).
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
  uploadIssuePhoto,
  deleteIssuePhoto,
  PHOTO_LIMITS,
} from '../services/issuePhotoService';

interface Props {
  isOpen: boolean;
  itemKey: string;
  itemLabel: string;
  checkId: number | null;
  itemId: number | null;
  initialNote: string;
  initialPhotos: string[];
  lang: 'el' | 'en';
  onClose: () => void;
  onSave: (note: string, photos: string[]) => void;
}

const t = (lang: 'el' | 'en', el: string, en: string) =>
  lang === 'el' ? el : en;

const IssueDetailsModal: React.FC<Props> = ({
  isOpen,
  itemKey,
  itemLabel,
  checkId,
  itemId,
  initialNote,
  initialPhotos,
  lang,
  onClose,
  onSave,
}) => {
  const [note, setNote] = useState(initialNote || '');
  const [photos, setPhotos] = useState<string[]>(initialPhotos || []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNote(initialNote || '');
      setPhotos(initialPhotos || []);
      setError(null);
      setCameraOpen(false);
    }
  }, [isOpen, itemKey, initialNote, initialPhotos]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (cameraOpen) closeCamera();
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cameraOpen, onClose]);

  useEffect(() => {
    if (!isOpen && streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canUpload = !!(checkId && itemId);
  const photosFull = photos.length >= PHOTO_LIMITS.MAX_PHOTOS;

  const openCamera = async () => {
    if (!canUpload || photosFull || uploading) return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 50);
    } catch (e: any) {
      console.error('Camera error', e);
      setError(
        t(
          lang,
          'Δεν ήταν δυνατό να ανοίξει η κάμερα. Έλεγξε ότι έδωσες δικαίωμα.',
          'Could not open camera. Check permission.'
        )
      );
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!canUpload) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setUploading(true);
    try {
      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          b => (b ? resolve(b) : reject(new Error('toBlob failed'))),
          'image/jpeg',
          0.85
        );
      });
      const file = new File([blob], `cam-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      const result = await uploadIssuePhoto(checkId!, itemId!, file);
      if (result.success === true) {
        setPhotos(result.photos);
      } else {
        setError((result as { success: false; error: string }).error);
      }
    } catch (e: any) {
      setError(e?.message || 'Capture failed');
    } finally {
      setUploading(false);
      closeCamera();
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!canUpload) {
      setError(
        t(
          lang,
          'Αποθήκευσε πρώτα τον έλεγχο για να ανεβάσεις φωτογραφίες.',
          'Save the check first before uploading photos.'
        )
      );
      return;
    }
    setError(null);
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        if (photos.length + 1 > PHOTO_LIMITS.MAX_PHOTOS) {
          setError(
            t(
              lang,
              `Μέγιστο ${PHOTO_LIMITS.MAX_PHOTOS} φωτογραφίες ανά πρόβλημα.`,
              `Max ${PHOTO_LIMITS.MAX_PHOTOS} photos per issue.`
            )
          );
          break;
        }
        const result = await uploadIssuePhoto(checkId!, itemId!, files[i]);
        if (result.success === true) {
          setPhotos(result.photos);
        } else {
          setError((result as { success: false; error: string }).error);
          break;
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async (path: string) => {
    if (!itemId) {
      setPhotos(p => p.filter(x => x !== path));
      return;
    }
    setUploading(true);
    setError(null);
    const r = await deleteIssuePhoto(itemId, path);
    setUploading(false);
    if (r.success && r.photos) {
      setPhotos(r.photos);
    } else {
      setError(r.error || 'Delete failed');
    }
  };

  const handleSave = () => {
    const trimmed = note.trim();
    if (trimmed.length < 3) {
      setError(
        t(
          lang,
          'Η περιγραφή πρέπει να έχει τουλάχιστον 3 χαρακτήρες.',
          'Description must have at least 3 characters.'
        )
      );
      return;
    }
    if (cameraOpen) closeCamera();
    onSave(trimmed, photos);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (cameraOpen) closeCamera();
          else onClose();
        }
      }}
    >
      <div
        style={{
          background: '#fff',
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          padding: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                color: '#dc2626',
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              ❌ {t(lang, 'ΠΡΟΒΛΗΜΑ', 'ISSUE')}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>
              {itemLabel}
            </div>
          </div>
          <button
            onClick={() => { if (cameraOpen) closeCamera(); else onClose(); }}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 22,
              lineHeight: 1,
              cursor: 'pointer',
              color: '#6b7280',
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {cameraOpen && (
          <div style={{ marginBottom: 14 }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                borderRadius: 8,
                background: '#000',
                maxHeight: 360,
                objectFit: 'cover',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                onClick={capturePhoto}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  background: uploading ? '#9ca3af' : '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                📸 {t(lang, 'Φωτογράφηση', 'Take photo')}
              </button>
              <button
                type="button"
                onClick={closeCamera}
                style={{
                  padding: '10px 14px',
                  background: '#e5e7eb',
                  color: '#111827',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {t(lang, 'Άκυρο', 'Cancel')}
              </button>
            </div>
          </div>
        )}

        {!cameraOpen && (
          <>
            <label
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                color: '#374151',
                marginBottom: 6,
              }}
            >
              {t(lang, 'Περιγραφή προβλήματος', 'Issue description')}{' '}
              <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder={t(
                lang,
                'Περιγράψτε αναλυτικά το πρόβλημα...',
                'Describe the issue in detail...'
              )}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 14,
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />

            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <label
                  style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}
                >
                  {t(lang, 'Φωτογραφίες', 'Photos')} ({photos.length}/
                  {PHOTO_LIMITS.MAX_PHOTOS})
                </label>
                {!canUpload && (
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    {t(
                      lang,
                      '(Αποθήκευσε πρώτα τον έλεγχο)',
                      '(Save the check first)'
                    )}
                  </span>
                )}
              </div>

              {photos.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  {photos.map((p) => (
                    <div
                      key={p}
                      style={{
                        position: 'relative',
                        paddingTop: '100%',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        overflow: 'hidden',
                        background: '#f9fafb',
                      }}
                    >
                      <img
                        src={'/' + p}
                        alt=""
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(p)}
                        title={t(lang, 'Διαγραφή', 'Delete')}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(220,38,38,0.92)',
                          color: '#fff',
                          border: 'none',
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          cursor: 'pointer',
                          fontSize: 14,
                          lineHeight: '20px',
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <button
                  type="button"
                  disabled={!canUpload || photosFull || uploading}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '8px 14px',
                    background:
                      !canUpload || photosFull || uploading
                        ? '#9ca3af'
                        : '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    cursor:
                      !canUpload || photosFull || uploading
                        ? 'not-allowed'
                        : 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  📁 {t(lang, 'Επιλογή αρχείων', 'Choose files')}
                </button>

                <button
                  type="button"
                  disabled={!canUpload || photosFull || uploading}
                  onClick={openCamera}
                  style={{
                    padding: '8px 14px',
                    background:
                      !canUpload || photosFull || uploading
                        ? '#9ca3af'
                        : '#059669',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    cursor:
                      !canUpload || photosFull || uploading
                        ? 'not-allowed'
                        : 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  📷 {t(lang, 'Κάμερα', 'Camera')}
                </button>

                {uploading && (
                  <span
                    style={{
                      fontSize: 12,
                      color: '#6b7280',
                      alignSelf: 'center',
                    }}
                  >
                    {t(lang, 'Μεταφόρτωση...', 'Uploading...')}
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!cameraOpen && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 18,
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: '8px 18px',
                background: '#e5e7eb',
                color: '#111827',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {t(lang, 'Άκυρο', 'Cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={uploading}
              style={{
                padding: '8px 18px',
                background: uploading ? '#9ca3af' : '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {t(lang, 'Αποθήκευση', 'Save')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueDetailsModal;
